import { MESSAGE_KEY, TEXT_STRING } from '../../../constants';
import { useNavigate } from 'react-router-dom';
import {
  EUserProvider,
  EGiftPaymentStep,
  EPointPaymentStep,
  TMember,
  TMessageDetail,
} from 'types';
import { Avatar, ChatMediaView, Text } from 'components';
import moment from 'moment';
import { useContext, useMemo } from 'react';
import Markdown from 'markdown-to-jsx';
import { ChatContext } from '../context';
import { getFileTypeFromUrl } from 'utils';
import { getSalonBadge } from 'components/Avatar/salonBadge';

type Props = {
  message: TMessageDetail;
  displayText: string;
  isEdited: boolean;
  isMyMessage: boolean;
  isAdminMessage: boolean;
  showAvatar: boolean;
  user?: TMember;
  onClickAvatar: () => void;
  onLoad?: () => void;
  channelId?: string;
};

const SYSTEM_CHAT_UUID = process.env.REACT_APP_SYSTEM_CHAT_UUID;
const paymentStr = TEXT_STRING.PAYMENT;
const paymentDeferralReqStr = TEXT_STRING.PAYMENT_DEFERRAL_REQUEST;
const commonStr = TEXT_STRING.COMMON;

const MessageItem = ({
  message,
  displayText = '',
  isEdited = false,
  isMyMessage,
  isAdminMessage,
  showAvatar,
  user,
  onClickAvatar,
  onLoad,
  channelId,
}: Props) => {
  // Hooks
  const navigate = useNavigate();

  // Context
  const {
    setGiftView,
    setHistoryPaymentId,
    setStepPaymentGift,
    setPointHistoryId,
    setPaymentStep,
  } = useContext(ChatContext);

  // Memo, callbacks
  const [gift, messageData, media] = useMemo(() => {
    const messageChat = message.message?.message || message.message;

    return [messageChat?.gift, messageChat?.data, messageChat?.media];
  }, [message]);

  const hasMultipleMedia = (media?.length || 0) > 1;

  const isAutoMessage = useMemo(
    () => SYSTEM_CHAT_UUID === message.uuid,
    [message.uuid],
  );
  const userSalonBadge =
    user?.provider === EUserProvider.MALE_APPLICATION &&
    !isAutoMessage &&
    !isAdminMessage
      ? getSalonBadge(user)
      : undefined;

  const messageTime = useMemo(
    () => moment(+message.timetoken / 10000).format('HH:mm'),
    [message.timetoken],
  );

  const paymentId = useMemo(
    () => messageData?.paymentId || messageData?.prepaidId,
    [messageData?.paymentId, messageData?.prepaidId],
  );

  const historyPaymentId = useMemo(
    () => messageData?.historyPaymentId,
    [messageData?.historyPaymentId],
  );

  const isRegister = useMemo(
    () => messageData?.isRegister,
    [messageData?.isRegister],
  );

  const msgLinks = useMemo(() => {
    const isPostPay = messageData?.key === MESSAGE_KEY.POST_PAY;
    const isBankPay = messageData?.key === MESSAGE_KEY.BANK_PAY;

    const paymentLinkText = (() => {
      if (messageData?.prepaidId) return paymentStr.TO_PAYMENT;
      if (isPostPay) return paymentDeferralReqStr.OPEN_POST_PAYMENT_PAGE;
      if (isBankPay) return paymentDeferralReqStr.OPEN_BANK_PAYMENT_PAGE;
      return paymentStr.CHOOSE_METHOD;
    })();

    const paymentLinkAction = () => {
      if (!paymentId) return;

      const base = `/payment/${paymentId}`;
      const query = isPostPay
        ? `?${MESSAGE_KEY.POST_PAY}`
        : isBankPay
          ? `?${MESSAGE_KEY.BANK_PAY}`
          : '';

      navigate(`${base}${query}`, { state: { channelId } });
    };

    const paymentLink = {
      show: paymentId,
      text: paymentLinkText,
      action: paymentLinkAction,
    };

    return [
      paymentLink,
      {
        show: messageData?.pointHistoryId,
        text: paymentStr.GO_TO_PAYMENT_PAGE,
        action: () => {
          setPaymentStep(EPointPaymentStep.PROCESSING_DETAIL);
          setPointHistoryId(messageData?.pointHistoryId);
        },
      },
      {
        show: historyPaymentId,
        text: commonStr.PAYMENT_HERE,
        action: () => {
          setHistoryPaymentId(historyPaymentId);
          setStepPaymentGift(EGiftPaymentStep.GIFTS_PREDICT);
        },
      },
      {
        show: isRegister,
        text: commonStr.NAVIGATE_TO_REGISTER_SCREEN,
        action: () => navigate(`/signup`),
      },
    ];
  }, [
    paymentId,
    messageData?.prepaidId,
    messageData?.key,
    messageData?.pointHistoryId,
    historyPaymentId,
    isRegister,
    navigate,
    channelId,
    setPaymentStep,
    setPointHistoryId,
    setHistoryPaymentId,
    setStepPaymentGift,
  ]);

  return (
    <div
      className={`flex flex-col ${
        isMyMessage ? 'justify-end' : 'items-start'
      } ${hasMultipleMedia ? 'w-full' : ''} break-all`}
    >
      <div
        className={`flex ${isMyMessage ? 'items-end justify-end' : 'items-start'} ${
          hasMultipleMedia ? 'w-full' : ''
        }`}
      >
        {!isMyMessage ? (
          <div
            className={`w-12 ${hasMultipleMedia ? 'flex-none' : ''} ${user?.inactive && user?.id ? 'opacity-50' : ''}`}
          >
            {showAvatar && (
              <Avatar
                circle
                url={
                  isAutoMessage || isAdminMessage
                    ? '/images/logo192.png'
                    : user?.avatar
                }
                onClick={
                  isAutoMessage || isAdminMessage || (user && !user?.inactive)
                    ? onClickAvatar
                    : undefined
                }
                salonBadge={userSalonBadge}
              />
            )}
          </div>
        ) : (
          <div className={`flex items-end mb-1 mx-3 gap-1`}>
            <Text fontSize={10} className="mr-3 mt-auto">
              {messageTime}
            </Text>
          </div>
        )}

        <div
          className={`flex flex-col max-w-[70%] ${
            hasMultipleMedia ? 'w-[70%] min-w-0 flex-none' : ''
          } ${hasMultipleMedia && !isMyMessage ? 'ml-3' : ''}`}
        >
          {isEdited && (
            <span
              className={`
          mb-1 text-[11px] text-neutral-400 leading-none select-none whitespace-nowrap
          ${isMyMessage ? 'self-end mr-3' : 'ml-3'}
        `}
            >
              {TEXT_STRING.COMMON.EDITED}
            </span>
          )}

          {/* Message content */}
          <div
            className={`${
              isMyMessage
                ? 'bg-primary-200'
                : isAutoMessage
                  ? 'bg-neutral-50'
                  : 'bg-transparent'
            } ${!isMyMessage && !hasMultipleMedia ? 'ml-3' : ''} rounded-xl border border-neutral-200`}
          >
            <div className="px-3 py-2 text-neutral-600 whitespace-pre-line">
              <Markdown
                options={{
                  wrapper: 'p',
                  forceInline: true,
                  overrides: {
                    a: {
                      component: (props) => (
                        <a
                          href={props.href}
                          target="_blank"
                          rel="noreferrer noopener"
                        >
                          {props.children}
                        </a>
                      ),
                    },
                  },
                }}
              >
                {displayText}
              </Markdown>
              {msgLinks.map(
                (msg, index) =>
                  msg.show && (
                    <Text
                      key={index}
                      onClick={msg.action}
                      className="cursor-pointer text-sky-400 underline text-[20px] my-2"
                    >
                      {msg.text}
                    </Text>
                  ),
              )}
            </div>
            {gift && (
              <>
                <img
                  src={gift.image}
                  onClick={() => {
                    setGiftView(gift);
                  }}
                  alt={gift.name}
                  className="px-2"
                />
                <Text center className="mb-6">
                  {TEXT_STRING.MESSAGE.GIVE_A_GIFT.replace(
                    '$name',
                    gift.name || '',
                  )}
                </Text>
              </>
            )}

            {media && (
              <ChatMediaView
                listMedia={media}
                roundedTop={displayText ? '' : 'rounded-t-xl'}
                onLoad={onLoad}
              />
            )}
            {message.url && (
              <ChatMediaView
                listMedia={[
                  {
                    url: message.url,
                    type: getFileTypeFromUrl(message.url),
                  },
                ]}
                roundedTop={displayText ? '' : 'rounded-t-xl'}
                onLoad={onLoad}
              />
            )}
          </div>
        </div>

        {!isMyMessage && (
          <Text fontSize={10} className="ml-3 mt-auto">
            {messageTime}
          </Text>
        )}
      </div>
    </div>
  );
};

export default MessageItem;
